/**
 * Profile – Form rendering helpers
 * Pure template functions for rendering form inputs (checkboxes, radios, dropdowns, etc.)
 */

export function renderSelectOptions(options: Array<{value: string, label: string}>, selectedValue: string | undefined | null): string {
  return options.map(opt =>
    `<option value="${opt.value}" ${selectedValue === opt.value ? 'selected' : ''}>${opt.label}</option>`
  ).join('');
}

export function renderPntaCheckbox(groupId: string, isPntaSelected: boolean): string {
  return `
    <label class="pnta-checkbox">
      <input
        type="checkbox"
        id="${groupId}_pnta"
        data-pnta-for-group="${groupId}"
        ${isPntaSelected ? 'checked' : ''}
      />
      Prefer not to answer
    </label>
  `;
}

// PNTA is rendered separately below the group
export function renderCheckboxGroup(name: string, options: Array<{value: string, label: string}>, selectedValues: string[] | undefined | null, dataField: string): string {
  const selected = selectedValues || [];
  const isPntaSelected = selected.includes('pnta');
  // Filter out 'pnta' - it will be rendered separately
  const visibleOptions = options.filter(opt => opt.value !== 'pnta');

  const checkboxes = visibleOptions.map(opt => {
    const isExclusive = opt.value === 'none';
    // Don't check any options when PNTA is selected
    const isChecked = !isPntaSelected && selected.includes(opt.value);
    return `
    <label class="checkbox-item">
      <input
        type="checkbox"
        name="${name}"
        value="${opt.value}"
        ${isChecked ? 'checked' : ''}
        data-field="${dataField}"
        ${isExclusive ? 'data-exclusive="true"' : ''}
      />
      ${opt.label}
    </label>
  `;
  }).join('');

  // Check if original options had 'pnta' - if so, render PNTA checkbox
  const hadPnta = options.some(opt => opt.value === 'pnta');
  return checkboxes + (hadPnta ? renderPntaCheckbox(name, isPntaSelected) : '');
}

// PNTA is rendered separately below the group
export function renderRadioGroup(
  fieldId: string,
  options: Array<{value: string, label: string}>,
  selectedValue: string | number | undefined | null,
  dataField: string,
  jsonPath: string | undefined,
  labelText: string
): string {
  const selected = selectedValue?.toString() || '';
  const isPntaSelected = selected === 'pnta';
  // Filter out empty placeholder options and 'pnta'
  const visibleOptions = options.filter(opt => opt.value !== '' && opt.value !== 'pnta');

  // Check if original options had 'pnta'
  const hadPnta = options.some(opt => opt.value === 'pnta');

  return `
    <div class="label-with-indicator">
      <span class="group-label">${labelText}</span>
      <span class="save-indicator" id="indicator-${fieldId}">Saved <i class="fas fa-check"></i></span>
    </div>
    <div class="radio-group" id="${fieldId}">
      ${visibleOptions.map(opt => `
        <label class="radio-item">
          <input
            type="radio"
            name="${fieldId}"
            value="${opt.value}"
            ${!isPntaSelected && selected === opt.value ? 'checked' : ''}
            data-field="${dataField}"
            ${jsonPath ? `data-json-path="${jsonPath}"` : ''}
          />
          <span class="radio-circle"></span>
          ${opt.label}
        </label>
      `).join('')}
    </div>
    ${hadPnta ? renderPntaCheckbox(fieldId, isPntaSelected) : ''}
  `;
}

// Returns the dropdown value and whether "other" text field should be shown
export function getDropdownWithOtherState(
  options: Array<{value: string, label: string}>,
  currentValue: string | undefined | null
): { selectedValue: string; otherText: string; showOther: boolean } {
  if (!currentValue) {
    return { selectedValue: '', otherText: '', showOther: false };
  }
  // Check if value matches a known option (excluding empty and 'other')
  const knownValues = options.map(o => o.value).filter(v => v && v !== 'other');
  if (knownValues.includes(currentValue)) {
    return { selectedValue: currentValue, otherText: '', showOther: false };
  }
  // Value doesn't match known options - it's a custom "other" value
  return { selectedValue: 'other', otherText: currentValue, showOther: true };
}

// PNTA is rendered separately below the group
export function renderRadioGroupWithOther(
  fieldId: string,
  options: Array<{value: string, label: string}>,
  currentValue: string | undefined | null,
  jsonPath: string,
  labelText: string
): string {
  const isPntaSelected = currentValue === 'pnta';
  // For state calculation, pass null if pnta so no radio appears selected
  const { selectedValue, otherText, showOther } = getDropdownWithOtherState(options, isPntaSelected ? null : currentValue);
  // Filter out empty placeholder options and 'pnta'
  const visibleOptions = options.filter(opt => opt.value !== '' && opt.value !== 'pnta');

  // Check if original options had 'pnta'
  const hadPnta = options.some(opt => opt.value === 'pnta');

  return `
    <div class="label-with-indicator">
      <span class="group-label">${labelText}</span>
      <span class="save-indicator" id="indicator-${fieldId}">Saved <i class="fas fa-check"></i></span>
    </div>
    <div class="radio-group radio-group-with-other" id="${fieldId}">
      ${visibleOptions.map(opt => `
        <label class="radio-item">
          <input
            type="radio"
            name="${fieldId}"
            value="${opt.value}"
            ${!isPntaSelected && selectedValue === opt.value ? 'checked' : ''}
            data-field="profile_json"
            data-json-path="${jsonPath}"
            ${opt.value === 'other' ? 'data-has-other="true"' : ''}
          />
          <span class="radio-circle"></span>
          ${opt.label}
        </label>
      `).join('')}
    </div>
    <div class="other-text-container" id="${fieldId}_other_container" style="${showOther && !isPntaSelected ? '' : 'display: none;'}">
      <input
        type="text"
        id="${fieldId}_other"
        class="input other-text-input"
        value="${otherText}"
        placeholder="Please specify"
        data-field="profile_json"
        data-json-path="${jsonPath}"
        data-is-other-field="true"
        data-parent-radio="${fieldId}"
      />
    </div>
    ${hadPnta ? renderPntaCheckbox(fieldId, isPntaSelected) : ''}
  `;
}

export function renderDropdownWithOther(
  fieldId: string,
  options: Array<{value: string, label: string}>,
  currentValue: string | undefined | null,
  jsonPath: string
): string {
  const { selectedValue, otherText, showOther } = getDropdownWithOtherState(options, currentValue);

  return `
    <div class="input-with-indicator">
      <select
        id="${fieldId}"
        class="input dropdown-with-other"
        data-field="profile_json"
        data-json-path="${jsonPath}"
        data-has-other="true"
      >
        ${renderSelectOptions(options, selectedValue)}
      </select>
      <span class="save-indicator" id="indicator-${fieldId}">Saved <i class="fas fa-check"></i></span>
    </div>
    <div class="other-text-container" id="${fieldId}_other_container" style="${showOther ? '' : 'display: none;'}">
      <input
        type="text"
        id="${fieldId}_other"
        class="input other-text-input"
        value="${otherText}"
        placeholder="Please specify"
        data-field="profile_json"
        data-json-path="${jsonPath}"
        data-is-other-field="true"
        data-parent-select="${fieldId}"
      />
    </div>
  `;
}

// Helper to get state for multi-select with "Other"
export function getMultiSelectWithOtherState(
  options: Array<{value: string, label: string}>,
  currentValues: string[] | undefined | null
): { selectedValues: string[]; otherText: string; showOther: boolean } {
  if (!currentValues || currentValues.length === 0) {
    return { selectedValues: [], otherText: '', showOther: false };
  }

  const knownValues = options.map(o => o.value);
  const selected: string[] = [];
  const otherValues: string[] = [];

  for (const val of currentValues) {
    if (knownValues.includes(val)) {
      selected.push(val);
    } else {
      // Custom value - goes to "other"
      otherValues.push(val);
    }
  }

  const hasOther = otherValues.length > 0;
  return {
    selectedValues: selected,
    otherText: otherValues.join(', '),
    showOther: hasOther
  };
}

// PNTA is rendered separately below the group
export function renderMultiSelectWithOther(
  fieldId: string,
  options: Array<{value: string, label: string}>,
  currentValues: string[] | undefined | null,
  jsonPath: string,
  labelText: string
): string {
  const isPntaSelected = currentValues?.includes('pnta') || false;
  // For state calculation, pass empty array if pnta so no checkboxes appear selected
  const { selectedValues, otherText, showOther } = getMultiSelectWithOtherState(options, isPntaSelected ? [] : currentValues);

  // Filter out 'pnta' and add "Other" option at the end
  const visibleOptions = options.filter(opt => opt.value !== 'pnta');
  const optionsWithOther = [...visibleOptions, { value: 'other', label: 'Other' }];

  // Check if original options had 'pnta'
  const hadPnta = options.some(opt => opt.value === 'pnta');

  return `
    <div class="label-with-indicator">
      <span class="group-label">${labelText}</span>
      <span class="save-indicator" id="indicator-${fieldId}">Saved <i class="fas fa-check"></i></span>
    </div>
    <div class="checkbox-group multiselect-with-other" id="${fieldId}">
      ${optionsWithOther.map(opt => {
        const isExclusive = opt.value === 'none';
        // Don't check any options when PNTA is selected
        const isChecked = !isPntaSelected && (opt.value === 'other' ? showOther : selectedValues.includes(opt.value));
        return `
        <label class="checkbox-item">
          <input
            type="checkbox"
            name="${fieldId}"
            value="${opt.value}"
            ${isChecked ? 'checked' : ''}
            data-field="profile_json"
            data-json-path="${jsonPath}"
            data-is-multiselect="true"
            ${opt.value === 'other' ? 'data-is-other-checkbox="true"' : ''}
            ${isExclusive ? 'data-exclusive="true"' : ''}
          />
          ${opt.label}
        </label>
      `;
      }).join('')}
    </div>
    <div class="other-text-container" id="${fieldId}_other_container" style="${showOther && !isPntaSelected ? '' : 'display: none;'}">
      <input
        type="text"
        id="${fieldId}_other"
        class="input other-text-input"
        value="${otherText}"
        placeholder="Please specify (comma-separated)"
        data-field="profile_json"
        data-json-path="${jsonPath}"
        data-is-other-field="true"
        data-parent-multiselect="${fieldId}"
        data-is-array="true"
      />
    </div>
    ${hadPnta ? renderPntaCheckbox(fieldId, isPntaSelected) : ''}
  `;
}

// Stores checked values in one field and custom text in a separate field
// PNTA is rendered separately below the group
export function renderMultiSelectWithCustomField(
  fieldId: string,
  options: Array<{value: string, label: string}>,
  currentValues: string[] | undefined | null,
  customValue: string | undefined | null,
  jsonPath: string,
  customJsonPath: string,
  labelText: string
): string {
  const selected = currentValues || [];
  const isPntaSelected = selected.includes('pnta');
  const hasCustom = !isPntaSelected && selected.includes('custom');

  // Filter out 'pnta' - it will be rendered separately
  const visibleOptions = options.filter(opt => opt.value !== 'pnta');

  // Check if original options had 'pnta'
  const hadPnta = options.some(opt => opt.value === 'pnta');

  return `
    <div class="label-with-indicator">
      <span class="group-label">${labelText}</span>
      <span class="save-indicator" id="indicator-${fieldId}">Saved <i class="fas fa-check"></i></span>
    </div>
    <div class="checkbox-group multiselect-with-custom" id="${fieldId}">
      ${visibleOptions.map(opt => {
        // Don't check any options when PNTA is selected
        const isChecked = !isPntaSelected && selected.includes(opt.value);
        return `
        <label class="checkbox-item">
          <input
            type="checkbox"
            name="${fieldId}"
            value="${opt.value}"
            ${isChecked ? 'checked' : ''}
            data-field="profile_json"
            data-json-path="${jsonPath}"
            data-is-multiselect="true"
            ${opt.value === 'custom' ? 'data-is-custom-checkbox="true"' : ''}
          />
          ${opt.label}
        </label>
      `;
      }).join('')}
    </div>
    <div class="custom-text-container" id="${fieldId}_custom_container" style="${hasCustom ? '' : 'display: none;'}">
      <input
        type="text"
        id="${fieldId}_custom"
        class="input custom-text-input"
        value="${customValue || ''}"
        placeholder="Enter your pronouns"
        data-field="profile_json"
        data-json-path="${customJsonPath}"
        data-is-custom-field="true"
        data-parent-multiselect="${fieldId}"
      />
    </div>
    ${hadPnta ? renderPntaCheckbox(fieldId, isPntaSelected) : ''}
  `;
}

export function renderVisiCheckbox(visiField: string, visiJson: Record<string, any>): string {
  return `
    <label class="pnta-checkbox">
      <input type="checkbox" data-visi-field="${visiField}" ${visiJson[visiField] === false ? 'checked' : ''} />
      Do not display on profile
    </label>
  `;
}
